import { useCallback, useEffect, useMemo, useState } from 'react';
import { CryptoFlag } from 'shared/src/CryptoFlag';
import { CryptoNetworkIcon } from 'shared/src/CryptoNetworkIcon';

import { hasValue } from 'utils/hasValue';

import type {
  GetCryptoBalances200,
  GetCryptoBalances200DataBalancesItem,
  GetCryptoDepositFromNetworks200DataNetworksItem,
  GetCryptoExchangeFromNetworks200DataNetworksItem
} from 'api/customerApi';
import {
  useGetCryptoBalances,
  useGetCryptoDepositFromCurrencies,
  useGetCryptoDepositFromNetworks,
  useGetCryptoExchangeFromCurrencies,
  useGetCryptoExchangeFromNetworks
} from 'api/customerApi';

import { useOrganisationId } from 'hooks/context';

import { useVirtualBankAccount } from './VirtualBankAccountProvider';

import { ASSET_NAMES, getNetworkDisplayName } from '../config';

type CryptoSelectionFlow = 'deposit' | 'exchange';
type CryptoSelectionOptions = {
  flow?: CryptoSelectionFlow;
  balances?: GetCryptoBalances200;
  shouldFetchBalances?: boolean;
};
type CryptoNetwork =
  | GetCryptoDepositFromNetworks200DataNetworksItem
  | GetCryptoExchangeFromNetworks200DataNetworksItem;
type CryptoNetworkBalance = {
  asset?: string;
  network?: string;
  protocol?: string | null;
  balance?: number;
  formatted_balance?: string;
};
type SelectedAssetLimits = {
  minAmount?: string | null;
  maxAmount?: string | null;
};

export const CRYPTO_SELECTOR_STALE_TIME = 5 * 60 * 1000;

const networkBalance = (network?: CryptoNetwork) => {
  if (!network || !('balance' in network)) return 0;

  return network.balance ?? 0;
};

const toNetworkBalance = (network: CryptoNetwork): CryptoNetworkBalance => ({
  asset: network.asset,
  network: network.network,
  protocol: network.protocol,
  balance: 'balance' in network ? network.balance : undefined,
  formatted_balance:
    'formatted_balance' in network ? network.formatted_balance : undefined
});

export const useCryptoSelection = ({
  flow = 'exchange',
  balances: sharedBalances,
  shouldFetchBalances = true
}: CryptoSelectionOptions = {}) => {
  const organisationId = useOrganisationId<number>();
  const { virtualAccountNumber } = useVirtualBankAccount();

  const [selectedAsset, setSelectedAsset] = useState<string>();
  const [selectedAddressId, setSelectedAddressId] = useState<string>();
  const providerQueryEnabled = hasValue(virtualAccountNumber);

  const { data: fetchedBalances } = useGetCryptoBalances(
    organisationId,
    { virtual_account_number: virtualAccountNumber },
    {
      query: {
        refetchInterval: 10_000,
        enabled: shouldFetchBalances && providerQueryEnabled,
        refetchOnWindowFocus: false
      }
    }
  );
  const balances = sharedBalances ?? fetchedBalances;

  const {
    data: depositFromCurrencies,
    isFetching: isFetchingDepositFromCurrencies
  } = useGetCryptoDepositFromCurrencies(
    organisationId,
    { virtual_account_number: virtualAccountNumber },
    {
      query: {
        enabled: flow === 'deposit' && providerQueryEnabled,
        staleTime: CRYPTO_SELECTOR_STALE_TIME,
        refetchOnWindowFocus: false
      }
    }
  );

  const {
    data: depositFromNetworks,
    isFetching: isFetchingDepositFromNetworks
  } = useGetCryptoDepositFromNetworks(
    organisationId,
    { virtual_account_number: virtualAccountNumber },
    {
      query: {
        enabled:
          flow === 'deposit' && providerQueryEnabled && hasValue(selectedAsset),
        staleTime: CRYPTO_SELECTOR_STALE_TIME,
        refetchOnWindowFocus: false
      }
    }
  );

  const {
    data: exchangeFromCurrencies,
    isFetching: isFetchingExchangeFromCurrencies
  } = useGetCryptoExchangeFromCurrencies(
    organisationId,
    { virtual_account_number: virtualAccountNumber },
    {
      query: {
        enabled: flow === 'exchange' && providerQueryEnabled,
        staleTime: CRYPTO_SELECTOR_STALE_TIME,
        refetchOnWindowFocus: false
      }
    }
  );

  const exchangeFromCurrencyList = useMemo(
    () =>
      flow === 'deposit'
        ? (depositFromCurrencies?.data?.currencies ?? [])
        : (exchangeFromCurrencies?.data?.currencies ?? []),
    [
      depositFromCurrencies?.data?.currencies,
      exchangeFromCurrencies?.data?.currencies,
      flow
    ]
  );

  const {
    data: exchangeFromNetworks,
    isFetching: isFetchingExchangeFromNetworks
  } = useGetCryptoExchangeFromNetworks(
    organisationId,
    { virtual_account_number: virtualAccountNumber },
    {
      query: {
        enabled: false,
        staleTime: CRYPTO_SELECTOR_STALE_TIME,
        refetchOnWindowFocus: false
      }
    }
  );

  const exchangeFromNetworkList = useMemo(
    () =>
      flow === 'deposit'
        ? (depositFromNetworks?.data?.networks ?? [])
        : (exchangeFromNetworks?.data?.networks ?? []),
    [
      depositFromNetworks?.data?.networks,
      exchangeFromNetworks?.data?.networks,
      flow
    ]
  );

  const networkBalanceByKey = useMemo(
    () =>
      new Map(
        exchangeFromNetworkList.map((item) => [
          `${item.asset}:${item.network}`,
          toNetworkBalance(item)
        ])
      ),
    [exchangeFromNetworkList]
  );

  const assetBalances = useMemo(
    () =>
      new Map(
        (balances?.data?.balances ?? []).map((item) => [
          item.asset,
          item.balance ?? 0
        ])
      ),
    [balances?.data?.balances]
  );

  const supportedAssets = useMemo(
    () =>
      exchangeFromCurrencyList
        .map((currency) => currency.asset)
        .filter(hasValue)
        .sort(
          (a, b) => (assetBalances.get(b) ?? 0) - (assetBalances.get(a) ?? 0)
        ),
    [exchangeFromCurrencyList, assetBalances]
  );

  const currencyOptions = useMemo(
    () =>
      supportedAssets.map((asset) => ({
        label: (
          <CurrencyOption
            asset={asset}
            ledgerBalances={balances?.data?.balances}
          />
        ),
        value: asset
      })),
    [balances?.data?.balances, supportedAssets]
  );
  const isCurrencyOptionsLoading = Boolean(
    flow === 'deposit'
      ? isFetchingDepositFromCurrencies
      : isFetchingExchangeFromCurrencies
  );
  const isNetworkOptionsLoading = Boolean(
    flow === 'deposit'
      ? isFetchingDepositFromNetworks
      : isFetchingExchangeFromNetworks
  );

  const networkOptions = useMemo(() => {
    if (!selectedAsset) return [];

    return exchangeFromNetworkList
      .filter((item) => item.asset === selectedAsset)
      .map((item) => item.network)
      .filter(hasValue)
      .sort(
        (a, b) =>
          networkBalance(networkBalanceByKey.get(`${selectedAsset}:${b}`)) -
          networkBalance(networkBalanceByKey.get(`${selectedAsset}:${a}`))
      )
      .map((network) => ({
        key: network,
        label: (
          <NetworkOptionLabel
            key={network}
            network={network}
            asset={selectedAsset}
            networkBalances={exchangeFromNetworkList}
          />
        ),
        value: network
      }));
  }, [selectedAsset, exchangeFromNetworkList, networkBalanceByKey]);

  const selectedNetwork = selectedAddressId;

  const selectedAssetLimits = useMemo<SelectedAssetLimits | undefined>(() => {
    if (!hasValue(selectedAsset)) return undefined;

    const currency = exchangeFromCurrencyList.find(
      (item) => item.asset === selectedAsset
    );

    if (!hasValue(currency)) return undefined;
    const currencyWithLimits = currency as Partial<{
      min_amount: string | null;
      max_amount: string | null;
    }>;

    return {
      minAmount: currencyWithLimits.min_amount,
      maxAmount: currencyWithLimits.max_amount
    };
  }, [selectedAsset, exchangeFromCurrencyList]);

  useEffect(() => {
    if (flow !== 'deposit') return;

    const hasSelectedNetwork = networkOptions.some(
      (option) => option.value === selectedAddressId
    );

    if (
      networkOptions.length > 0 &&
      hasValue(selectedAsset) &&
      (!hasValue(selectedAddressId) || !hasSelectedNetwork)
    ) {
      setSelectedAddressId(networkOptions[0].value);
    }
  }, [flow, networkOptions, selectedAsset, selectedAddressId]);

  const handleCurrencyChange = useCallback((value: string) => {
    setSelectedAsset(value);
    setSelectedAddressId(undefined);
  }, []);

  const reset = useCallback(() => {
    setSelectedAsset(undefined);
    setSelectedAddressId(undefined);
  }, []);

  return {
    organisationId,
    balances,
    currencyOptions,
    isCurrencyOptionsLoading,
    isNetworkOptionsLoading,
    networkOptions,
    networkBalanceByKey,
    selectedAsset,
    selectedAddressId,
    setSelectedAddressId,
    selectedNetwork,
    selectedAssetLimits,
    handleCurrencyChange,
    reset
  };
};

export const CurrencyLabel = ({
  asset,
  ledgerBalances
}: {
  asset: string;
  ledgerBalances?: GetCryptoBalances200DataBalancesItem[];
}) => {
  const assetBalance = useMemo(
    () => ledgerBalances?.find((b) => b.asset === asset),
    [ledgerBalances, asset]
  );

  return (
    <span className="mt-1 inline-flex w-full items-center justify-between gap-2">
      <span className="inline-flex items-center gap-2">
        <CryptoFlag cryptoCode={asset} className="size-6" />
        <span>{asset}</span>
      </span>
      {hasValue(ledgerBalances) && (
        <span className="text-sm font-medium text-content-primary">
          {assetBalance?.formatted_balance ?? '0'} {asset}
        </span>
      )}
    </span>
  );
};

export const CurrencyOption = ({
  asset,
  ledgerBalances
}: {
  asset: string;
  ledgerBalances?: GetCryptoBalances200DataBalancesItem[];
}) => {
  const assetBalance = useMemo(
    () => ledgerBalances?.find((b) => b.asset === asset),
    [ledgerBalances, asset]
  );

  return (
    <div className="flex w-full items-center justify-between py-0.5">
      <div className="flex items-center gap-2">
        <CryptoFlag cryptoCode={asset} className="size-6" />
        <div className="flex flex-col leading-tight">
          <span className="text-sm">{ASSET_NAMES[asset] ?? asset}</span>
          <span className="text-xs text-content-secondary">{asset}</span>
        </div>
      </div>
      {hasValue(ledgerBalances) && (
        <span className="text-sm font-medium text-content-primary">
          {assetBalance?.formatted_balance ?? '0'} {asset}
        </span>
      )}
    </div>
  );
};

const NetworkOptionLabel = ({
  network,
  asset,
  networkBalances
}: {
  network: string;
  asset: string;
  networkBalances?: CryptoNetwork[];
}) => {
  const currencyBalance = useMemo(
    () =>
      networkBalances?.find((b) => b.asset === asset && b.network === network),
    [networkBalances, asset, network]
  );
  const formattedBalance =
    currencyBalance && 'formatted_balance' in currencyBalance
      ? currencyBalance.formatted_balance
      : undefined;

  return (
    <span className="mt-1 inline-flex w-full items-center gap-2">
      <CryptoNetworkIcon network={network} />
      <span>{getNetworkDisplayName(network)}</span>
      {hasValue(formattedBalance) && (
        <span className="ml-auto font-medium text-content-primary">
          {formattedBalance} {asset}
        </span>
      )}
    </span>
  );
};
